// src/modules/operaciones/inventarios/InventariosInformes.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { FileText, Save, Trash2, Loader2, RotateCcw, Database, Eye, X } from "lucide-react";
import Swal from "sweetalert2";
import UploadInforme from "./components/UploadInforme";
import useInforme from "./hooks/useInforme";
import { fmtFecha, etiquetaTipo, fmtNumero } from "./utils/informesComun";
import InformeViewer from "./components/InformeViewer";

export default function InventariosInformes({ onBackToMenu }) {
  const {
    estado, error, parseado, archivoOriginal, guardados = [], cargandoInforme,
    subirArchivo, guardarInforme, cargarGuardados, cargarInforme, eliminarInforme, reset,
  } = useInforme();

  const listaSegura = Array.isArray(guardados) ? guardados : [];
  const [nombreInforme, setNombreInforme] = useState("");
  const [guardando, setGuardando] = useState(false);
  // Informe guardado que está abierto en el dashboard: { id, nombre } | null
  const [abierto, setAbierto] = useState(null);

  // CANDADO PARA EVITAR EL BUCLE INFINITO
  const yaCargo = useRef(false);
  const visorRef = useRef(null);

  useEffect(() => { 
    if (!yaCargo.current) {
      yaCargo.current = true;
      cargarGuardados(); 
    }
  }, [cargarGuardados]);

  // Nombre sugerido solo cuando el informe viene de un archivo subido desde el equipo
  useEffect(() => {
    if (estado === "ready" && archivoOriginal && !nombreInforme) {
      setNombreInforme(archivoOriginal.name.replace(/\.[^.]+$/, ""));
    }
  }, [estado, archivoOriginal, nombreInforme]);

  // Subir un archivo nuevo: se suelta el informe guardado abierto y el nombre anterior
  const handleArchivo = useCallback((archivo) => {
    setAbierto(null);
    setNombreInforme("");
    subirArchivo(archivo);
  }, [subirArchivo]);

  const handleReset = useCallback(() => {
    setAbierto(null);
    setNombreInforme("");
    reset();
  }, [reset]);

  // Abrir un informe guardado desde la lista
  const handleAbrir = useCallback(async (inf) => {
    if (!inf?.id || cargandoInforme) return;
    const ok = await cargarInforme(inf.id);
    if (ok) {
      setAbierto({ id: inf.id, nombre: inf.nombre || "Sin nombre" });
      setNombreInforme("");
      requestAnimationFrame(() => {
        visorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [cargarInforme, cargandoInforme]);

  const handleGuardar = useCallback(async () => {
    if (!nombreInforme.trim()) {
      Swal.fire("Falta el nombre", "Escribe un nombre para el informe.", "warning");
      return;
    }
    setGuardando(true);
    try {
      await guardarInforme({ nombre: nombreInforme.trim(), incluirFilas: true });
      Swal.fire("Guardado", "El informe se guardó correctamente.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "No se pudo guardar el informe.", "error");
    } finally {
      setGuardando(false);
    }
  }, [nombreInforme, guardarInforme]);

  const handleEliminar = useCallback(async (id, nombre) => {
    const result = await Swal.fire({
      title: "¿Eliminar informe?",
      text: `Se eliminará "${nombre}". Esta acción no se puede deshacer.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar", confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      await eliminarInforme(id);
      // Si era el informe que está abierto, se cierra el dashboard
      if (abierto && String(abierto.id) === String(id)) handleReset();
      Swal.fire("Eliminado", "El informe se eliminó.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "No se pudo eliminar.", "error");
    }
  }, [eliminarInforme, abierto, handleReset]);

  // ¿El dashboard viene de un archivo subido o de la base de datos?
  const desdeArchivo = Boolean(archivoOriginal);
  const desdeBD = Boolean(abierto) && !desdeArchivo;
  const hayInforme = estado === "ready" && Boolean(parseado);
  const mostrarVisor = hayInforme || cargandoInforme;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="max-w-[95%] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-800 flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-600" /> Informes de Inventario
            </h1>
            <p className="text-sm text-gray-500 mt-1">Sube un documento, analízalo y guarda el informe generado.</p>
          </div>
          <button onClick={onBackToMenu} className="px-5 py-2.5 rounded-xl bg-white text-gray-700 text-sm font-bold shadow-sm border border-gray-200 hover:bg-gray-50 transition-all cursor-pointer">
            ← Volver al Menú
          </button>
        </div>

        {/* Tarjeta de carga */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">1. Sube el documento</h2>
          <UploadInforme
            onArchivo={handleArchivo}
            cargando={estado === "uploading" || estado === "parsing"}
            error={error}
            tipo={parseado?.meta?.tipo || null}
          />

          {/* Solo para archivos subidos desde el equipo: nombre + guardar */}
          {hayInforme && desdeArchivo && (
            <div className="mt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-700">Documento procesado correctamente</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {etiquetaTipo(parseado.meta?.tipo)} · {fmtNumero(parseado.filasTotales)} filas · {fmtNumero(parseado.columnas?.length)} columnas
                  </p>
                </div>
                <button onClick={handleReset} className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1.5 cursor-pointer">
                  <RotateCcw className="w-3.5 h-3.5" /> Cargar otro archivo
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={nombreInforme}
                  onChange={(e) => setNombreInforme(e.target.value)}
                  placeholder="Nombre del informe"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-400"
                />
                <button
                  onClick={handleGuardar}
                  disabled={guardando}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {guardando ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : <><Save className="w-4 h-4" /> Guardar informe</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Dashboard (archivo recién subido o informe guardado) */}
        {mostrarVisor && (
          <div ref={visorRef} className="mb-6 scroll-mt-4">
            <InformeViewer
              datos={parseado}
              cargando={cargandoInforme}
              titulo={desdeBD ? abierto.nombre : ""}
              acciones={
                desdeBD ? (
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium cursor-pointer"
                  >
                    <X className="w-4 h-4" /> Cerrar informe
                  </button>
                ) : null
              }
            />
          </div>
        )}

        {/* Lista de informes guardados */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-gray-500" /> 2. Informes guardados ({listaSegura.length})
          </h2>
          
          {listaSegura.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No hay informes guardados todavía.</p>
          ) : (
            <div className="space-y-2">
              {listaSegura.map((inf) => {
                const esAbierto = desdeBD && String(abierto.id) === String(inf.id);
                return (
                  <div
                    key={inf.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition ${
                      esAbierto ? "bg-blue-50 border-blue-300" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleAbrir(inf)}
                      disabled={cargandoInforme}
                      title="Abrir informe"
                      className="min-w-0 flex-1 text-left cursor-pointer disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                    >
                      <p className="font-bold text-sm text-gray-800 truncate">{inf.nombre || "Sin nombre"}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {etiquetaTipo(inf.tipo)} · {fmtNumero(inf.filasTotales)} filas · Guardado {fmtFecha(inf.creadoEn)}
                      </p>
                    </button>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleAbrir(inf)}
                        disabled={cargandoInforme}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-blue-700 hover:bg-blue-100 text-xs font-bold cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                        title="Abrir informe"
                      >
                        {cargandoInforme && esAbierto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        Ver
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEliminar(inf.id, inf.nombre)}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer"
                        title="Eliminar informe"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}